import { useState, useEffect } from 'react';
import {
  GraduationCap,
  Edit2,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  Loader2,
  History,
  ChevronDown,
  ChevronUp,
  ExternalLink
} from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';

interface FrameworkLevel {
  id: string;
  framework: 'NKR' | 'EQF';
  level: number;
  level_name: string;
  level_name_en?: string;
  knowledge_descriptor: string;
  skills_descriptor: string;
  competence_descriptor: string;
  source_document: string;
  source_url?: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
  updated_by?: string;
}

interface HistoryEntry {
  id: string;
  field_changed: string;
  old_value: string;
  new_value: string;
  changed_at: string;
  change_reason: string;
  changed_by_name?: string;
  changed_by_email?: string;
}

interface EditFormData {
  knowledge_descriptor: string;
  skills_descriptor: string;
  competence_descriptor: string;
  source_document: string;
  source_url: string;
  change_reason: string;
}

export default function QualificationFrameworkSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [nkrLevels, setNkrLevels] = useState<FrameworkLevel[]>([]);
  const [eqfLevels, setEqfLevels] = useState<FrameworkLevel[]>([]);
  const [activeFramework, setActiveFramework] = useState<'NKR' | 'EQF'>('NKR');

  const [editingLevel, setEditingLevel] = useState<FrameworkLevel | null>(null);
  const [editForm, setEditForm] = useState<EditFormData>({
    knowledge_descriptor: '',
    skills_descriptor: '',
    competence_descriptor: '',
    source_document: '',
    source_url: '',
    change_reason: ''
  });

  const [expandedLevel, setExpandedLevel] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState<HistoryEntry[]>([]);

  // Fetch framework levels
  const fetchLevels = async () => {
    try {
      setLoading(true);
      const response = await authenticatedFetch('/api/admin/qualification-frameworks');

      if (!response.ok) {
        throw new Error('Failed to fetch qualification frameworks');
      }

      const data = await response.json();
      setNkrLevels(data.nkr?.levels || []);
      setEqfLevels(data.eqf?.levels || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load qualification frameworks');
    } finally {
      setLoading(false);
    }
  };

  // Fetch history for a level
  const fetchHistory = async (levelId: string) => {
    try {
      setHistoryLoading(true);
      const response = await authenticatedFetch(
        `/api/admin/qualification-frameworks/history/${levelId}`
      );

      if (response.ok) {
        const data = await response.json();
        setHistory(data.history || []);
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchLevels();
  }, []);

  // Toggle expanded level and load history
  const toggleExpanded = async (levelId: string) => {
    if (expandedLevel === levelId) {
      setExpandedLevel(null);
      setHistory([]);
    } else {
      setExpandedLevel(levelId);
      await fetchHistory(levelId);
    }
  };

  // Start editing a level
  const startEditing = (level: FrameworkLevel) => {
    setEditingLevel(level);
    setEditForm({
      knowledge_descriptor: level.knowledge_descriptor,
      skills_descriptor: level.skills_descriptor,
      competence_descriptor: level.competence_descriptor,
      source_document: level.source_document || '',
      source_url: level.source_url || '',
      change_reason: ''
    });
    setError('');
    setSuccess('');
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingLevel(null);
    setEditForm({
      knowledge_descriptor: '',
      skills_descriptor: '',
      competence_descriptor: '',
      source_document: '',
      source_url: '',
      change_reason: ''
    });
  };

  // Save changes
  const saveChanges = async () => {
    if (!editingLevel) return;

    if (!editForm.change_reason.trim() || editForm.change_reason.trim().length < 5) {
      setError('Du må oppgi en endringsårsak (minimum 5 tegn)');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const response = await authenticatedFetch(
        `/api/admin/qualification-frameworks/level/${editingLevel.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            knowledge_descriptor: editForm.knowledge_descriptor,
            skills_descriptor: editForm.skills_descriptor,
            competence_descriptor: editForm.competence_descriptor,
            source_document: editForm.source_document,
            source_url: editForm.source_url,
            change_reason: editForm.change_reason
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save changes');
      }

      const result = await response.json();

      setSuccess(`${editingLevel.framework} Nivå ${editingLevel.level} oppdatert (${result.changes} felt endret)`);
      cancelEditing();
      await fetchLevels();

    } catch (err: any) {
      setError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const currentLevels = activeFramework === 'NKR' ? nkrLevels : eqfLevels;

  // Field label mapping
  const fieldLabels: Record<string, string> = {
    knowledge_descriptor: 'Kunnskap',
    skills_descriptor: 'Ferdigheter',
    competence_descriptor: 'Generell kompetanse',
    source_document: 'Kildedokument',
    source_url: 'Kilde-URL'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-kf-primary" size={32} />
        <span className="ml-3 text-gray-600">Laster kvalifikasjonsrammeverk...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <GraduationCap className="text-kf-primary" size={24} />
            Kvalifikasjonsrammeverk
          </h2>
          <p className="text-gray-600 text-sm mt-1">
            NKR og EQF nivådeskriptorer brukt i KI-validering av studieplaner
          </p>
        </div>
      </div>

      {/* Status messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-red-800 font-medium">Feil</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={20} />
          <p className="text-green-800">{success}</p>
        </div>
      )}

      {/* Info box */}
      <div className="bg-kf-blue-50 border border-kf-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <AlertCircle className="text-kf-blue-500 flex-shrink-0 mt-0.5" size={18} />
          <div className="text-sm text-kf-blue-800">
            <p className="font-medium mb-1">Om kvalifikasjonsrammeverk</p>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>NKR</strong> (Nasjonalt kvalifikasjonsrammeverk) - Norske nivåer 2-8</li>
              <li><strong>EQF</strong> (European Qualifications Framework) - Europeiske nivåer 1-8</li>
              <li>Deskriptorene brukes av KI-valideringen for å vurdere om læringsutbyttene er på riktig nivå</li>
              <li>Endringer logges og kan spores via historikk</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Framework tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveFramework('NKR')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeFramework === 'NKR'
                ? 'border-kf-primary text-kf-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            NKR (Norsk)
            <span className="ml-2 text-xs text-gray-400">Nivå 2-8</span>
          </button>
          <button
            onClick={() => setActiveFramework('EQF')}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeFramework === 'EQF'
                ? 'border-kf-primary text-kf-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            EQF (Europeisk)
            <span className="ml-2 text-xs text-gray-400">Nivå 1-8</span>
          </button>
        </nav>
      </div>

      {/* Level cards */}
      <div className="space-y-4">
        {currentLevels.map(level => (
          <div
            key={level.id}
            className="bg-white border border-gray-200 rounded-lg overflow-hidden"
          >
            {/* Level header */}
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-kf-primary text-white text-sm font-bold">
                    {level.level}
                  </span>
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {level.level_name}
                      {level.level_name_en && level.framework === 'EQF' && (
                        <span className="text-gray-500 font-normal ml-2">
                          ({level.level_name_en})
                        </span>
                      )}
                    </h3>
                    <p className="text-xs text-gray-500">
                      {level.source_document}
                      {level.source_url && (
                        <a
                          href={level.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-kf-primary hover:underline inline-flex items-center gap-1"
                        >
                          Kilde <ExternalLink size={10} />
                        </a>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleExpanded(level.id)}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                    title="Vis historikk"
                  >
                    <History size={18} />
                  </button>
                  <button
                    onClick={() => startEditing(level)}
                    className="p-2 text-gray-400 hover:text-kf-primary transition-colors"
                    title="Rediger"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => toggleExpanded(level.id)}
                    className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {expandedLevel === level.id ? (
                      <ChevronUp size={18} />
                    ) : (
                      <ChevronDown size={18} />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Level content */}
            <div className="p-4 space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">Kunnskap</h4>
                <p className="text-sm text-gray-600">{level.knowledge_descriptor}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">Ferdigheter</h4>
                <p className="text-sm text-gray-600">{level.skills_descriptor}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-1">Generell kompetanse</h4>
                <p className="text-sm text-gray-600">{level.competence_descriptor}</p>
              </div>
            </div>

            {/* Expanded history section */}
            {expandedLevel === level.id && (
              <div className="border-t border-gray-200 bg-gray-50 p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                  <History size={16} />
                  Endringshistorikk
                </h4>

                {historyLoading ? (
                  <div className="flex items-center gap-2 text-gray-500 text-sm">
                    <Loader2 className="animate-spin" size={14} />
                    Laster historikk...
                  </div>
                ) : history.length === 0 ? (
                  <p className="text-sm text-gray-500">Ingen endringer registrert</p>
                ) : (
                  <div className="space-y-3">
                    {history.map(entry => (
                      <div
                        key={entry.id}
                        className="bg-white rounded border border-gray-200 p-3 text-sm"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-medium text-gray-700">
                            {fieldLabels[entry.field_changed] || entry.field_changed}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(entry.changed_at).toLocaleString('nb-NO')}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mb-2">
                          Av: {entry.changed_by_name || entry.changed_by_email || 'Ukjent'}
                        </div>
                        <div className="text-xs text-gray-600 italic mb-2">
                          Årsak: {entry.change_reason}
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-gray-500">Fra:</span>
                            <p className="text-gray-700 line-clamp-2">{entry.old_value}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Til:</span>
                            <p className="text-gray-700 line-clamp-2">{entry.new_value}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Edit modal */}
      {editingLevel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                Rediger {editingLevel.framework} Nivå {editingLevel.level}: {editingLevel.level_name}
              </h3>
              <button
                onClick={cancelEditing}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kunnskapsbeskrivelse
                </label>
                <textarea
                  value={editForm.knowledge_descriptor}
                  onChange={e => setEditForm({ ...editForm, knowledge_descriptor: e.target.value })}
                  rows={4}
                  className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-kf-primary focus:border-kf-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ferdighetsbeskrivelse
                </label>
                <textarea
                  value={editForm.skills_descriptor}
                  onChange={e => setEditForm({ ...editForm, skills_descriptor: e.target.value })}
                  rows={4}
                  className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-kf-primary focus:border-kf-primary"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kompetansebeskrivelse
                </label>
                <textarea
                  value={editForm.competence_descriptor}
                  onChange={e => setEditForm({ ...editForm, competence_descriptor: e.target.value })}
                  rows={4}
                  className="w-full border border-gray-300 rounded-md p-3 text-sm focus:ring-kf-primary focus:border-kf-primary"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kildedokument
                  </label>
                  <input
                    type="text"
                    value={editForm.source_document}
                    onChange={e => setEditForm({ ...editForm, source_document: e.target.value })}
                    className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-kf-primary focus:border-kf-primary"
                    placeholder="F.eks. NKR 2011, oppdatert 2025"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kilde-URL (valgfritt)
                  </label>
                  <input
                    type="url"
                    value={editForm.source_url}
                    onChange={e => setEditForm({ ...editForm, source_url: e.target.value })}
                    className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-kf-primary focus:border-kf-primary"
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Årsak til endring *
                </label>
                <input
                  type="text"
                  value={editForm.change_reason}
                  onChange={e => setEditForm({ ...editForm, change_reason: e.target.value })}
                  className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-kf-primary focus:border-kf-primary"
                  placeholder="F.eks. Oppdatert iht. ny NKR-revisjon 2025"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Beskriv hvorfor du gjør denne endringen (minimum 5 tegn)
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                  <AlertCircle className="text-red-500 flex-shrink-0" size={16} />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 flex justify-end gap-3">
              <button
                onClick={cancelEditing}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                Avbryt
              </button>
              <button
                onClick={saveChanges}
                disabled={saving || !editForm.change_reason.trim()}
                className="px-4 py-2 bg-kf-primary text-white rounded-md hover:bg-kf-primary-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
              >
                {saving ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    Lagrer...
                  </>
                ) : (
                  <>
                    <Save size={16} />
                    Lagre endringer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
